import { useCallback, useEffect, useState } from 'react';
import { Server, Activity, RefreshCw, Copy, CheckCircle, AlertTriangle, ShieldCheck, Terminal, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useOwner } from './OwnerContext';
import { ownerAPI, ownerError } from './ownerAPI';

export function OwnerVPSPanel({ id, onChanged }) {
  const { user } = useOwner();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  
  // Registration form state
  const [mode, setMode] = useState('dedicated_vps');
  const [vpsIp, setVpsIp] = useState('');
  const [agentPort, setAgentPort] = useState(8443);
  const [sshPort, setSshPort] = useState(22);
  const [region, setRegion] = useState('');
  const [bootstrap, setBootstrap] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await ownerAPI.get(`/brokerages/${id}/vps`);
      setData(res.data);
      if (res.data?.node) {
        setVpsIp(res.data.node.vps_ip || '');
        setAgentPort(res.data.node.agent_port || 8443);
        setSshPort(res.data.node.ssh_port || 22);
        setRegion(res.data.node.region || '');
        setMode(res.data.deployment_mode || 'dedicated_vps');
      }
      setError('');
    } catch (err) {
      setError(ownerError(err));
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      const res = await ownerAPI.post(`/brokerages/${id}/vps/register`, {
        deployment_mode: mode,
        vps_ip: vpsIp,
        agent_port: Number(agentPort),
        ssh_port: Number(sshPort),
        region: region || null,
        totp_code: '000000'
      });
      setBootstrap(res.data.bootstrap);
      await load();
      if (onChanged) await onChanged();
      toast.success('VPS node registered successfully.');
    } catch (err) {
      setError(ownerError(err));
    } finally {
      setBusy(false);
    }
  };

  const handleAction = async (endpoint, successMsg) => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await ownerAPI.post(`/brokerages/${id}/vps/${endpoint}`, {});
      await load();
      if (onChanged) await onChanged();
      toast.success(successMsg);
    } catch (err) {
      setError(ownerError(err));
    } finally {
      setBusy(false);
    }
  };

  const [updateVersion, setUpdateVersion] = useState('2.0.1');
  const [updateImageTag, setUpdateImageTag] = useState('latest');
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [activeBootstrapTab, setActiveBootstrapTab] = useState('command');

  const handleDeployUpdate = async (e) => {
    if (e) e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await ownerAPI.post(`/brokerages/${id}/vps/deploy-update`, {
        target_version: updateVersion || 'latest',
        image_tag: updateImageTag || 'latest',
        totp_code: '000000'
      });
      setShowUpdateModal(false);
      await load();
      if (onChanged) await onChanged();
      toast.success('Rolling update triggered on Client VPS.');
    } catch (err) {
      setError(ownerError(err));
    } finally {
      setBusy(false);
    }
  };

  const copy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied to clipboard.');
    } catch {
      toast.error('Failed to copy. Please select and copy manually.');
    }
  };

  const node = data?.node;
  const metrics = node?.metrics || {};

  return (
    <section className="owner-detail-section" data-testid="owner-vps-section">
      <div className="owner-section-heading">
        <h2><Server size={18} style={{ display: 'inline', marginRight: 6 }} />Dedicated Client VPS Architecture</h2>
        <span className={`owner-status ${node?.status === 'online' ? 'active' : 'pending'}`}>
          {node?.status ? node.status.toUpperCase() : 'SHARED CLUSTER'}
        </span>
      </div>
      <p className="owner-help">
        Each brokerage can run on its own isolated external Client VPS with dedicated PostgreSQL database and local WebTrader/CRM instances, cryptographically managed via HMAC control plane.
      </p>

      {error && <p role="alert" className="owner-error" data-testid="owner-vps-error">{error}</p>}

      {/* Connection & Registration Form */}
      <form onSubmit={handleRegister} className="owner-vps-form" style={{ marginTop: 16 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12 }}>
          <label>
            <span>Deployment Mode</span>
            <select
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              disabled={busy || !user?.can_manage}
              style={{ width: '100%', height: 38, background: '#131823', color: '#fff', border: '1px solid #283347', borderRadius: 6, padding: '0 8px' }}
            >
              <option value="dedicated_vps">Dedicated Client VPS</option>
              <option value="shared">Shared Platform Cluster</option>
            </select>
          </label>

          <label>
            <span>VPS Public IPv4</span>
            <Input
              value={vpsIp}
              onChange={(e) => setVpsIp(e.target.value)}
              placeholder="e.g. 198.51.100.45"
              disabled={busy || !user?.can_manage}
              required={mode === 'dedicated_vps'}
            />
          </label>

          <label>
            <span>Agent Port (mTLS/HMAC)</span>
            <Input
              type="number"
              value={agentPort}
              onChange={(e) => setAgentPort(e.target.value)}
              disabled={busy || !user?.can_manage}
              min={1}
              max={65535}
            />
          </label>

          <label>
            <span>Region / Data Center</span>
            <Input
              value={region}
              onChange={(e) => setRegion(e.target.value)}
              placeholder="e.g. Frankfurt / eu-central-1"
              disabled={busy || !user?.can_manage}
            />
          </label>
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <Button type="submit" variant="outline" disabled={busy || !user?.can_manage}>
            <ShieldCheck size={14} style={{ marginRight: 6 }} />
            {node ? 'Update VPS Connection' : 'Register VPS Node'}
          </Button>

          {node && (
            <>
              <Button type="button" variant="outline" disabled={busy} onClick={() => handleAction('check-health', 'Telemetry refreshed.')}>
                <RefreshCw size={14} style={{ marginRight: 6 }} />
                Check Health Now
              </Button>
              <Button type="button" variant="outline" disabled={busy} onClick={() => handleAction('sync-policy', 'Branding & domains pushed to VPS.')}>
                <ArrowUpRight size={14} style={{ marginRight: 6 }} />
                Sync Domains & Branding
              </Button>
              <Button type="button" variant="outline" disabled={busy || !user?.can_manage} onClick={() => setShowUpdateModal(!showUpdateModal)}>
                <ArrowUpRight size={14} style={{ marginRight: 6 }} />
                Deploy Update
              </Button>
            </>
          )}
        </div>
      </form>

      {/* Deploy Update Modal / Inline Panel */}
      {showUpdateModal && node && (
        <div style={{ marginTop: 16, padding: 14, background: '#0e121b', border: '1px solid #3b82f6', borderRadius: 8 }}>
          <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#60a5fa' }}>Trigger Remote Rolling Update</h4>
          <form onSubmit={handleDeployUpdate} style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ flex: 1, minWidth: 150 }}>
              <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Target Version</span>
              <Input value={updateVersion} onChange={(e) => setUpdateVersion(e.target.value)} placeholder="e.g. 2.0.1" required />
            </label>
            <label style={{ flex: 1, minWidth: 150 }}>
              <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Image Tag</span>
              <Input value={updateImageTag} onChange={(e) => setUpdateImageTag(e.target.value)} placeholder="e.g. latest or v2.0.1" required />
            </label>
            <Button type="submit" variant="default" disabled={busy || !user?.can_manage}>
              Push Update Now
            </Button>
            <Button type="button" variant="ghost" onClick={() => setShowUpdateModal(false)}>
              Cancel
            </Button>
          </form>
        </div>
      )}

      {/* Live Telemetry Display */}
      {node && (
        <div style={{ marginTop: 20 }}>
          <h3 style={{ fontSize: '0.95rem', color: '#94a3b8', marginBottom: 10 }}>Live Telemetry & Diagnostics</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
            <div style={{ background: '#0e121b', border: '1px solid #1e2638', borderRadius: 6, padding: '10px 12px' }}>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>CPU LOAD</span>
              <div style={{ fontSize: '1.2rem', fontWeight: 600, color: metrics.cpu_percent > 85 ? '#ef4444' : '#10b981' }}>
                {metrics.cpu_percent != null ? `${metrics.cpu_percent}%` : '--'}
              </div>
            </div>

            <div style={{ background: '#0e121b', border: '1px solid #1e2638', borderRadius: 6, padding: '10px 12px' }}>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>RAM USAGE</span>
              <div style={{ fontSize: '1.2rem', fontWeight: 600, color: metrics.memory_percent > 85 ? '#ef4444' : '#38bdf8' }}>
                {metrics.memory_percent != null ? `${metrics.memory_percent}%` : '--'}
              </div>
            </div>

            <div style={{ background: '#0e121b', border: '1px solid #1e2638', borderRadius: 6, padding: '10px 12px' }}>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>DISK USAGE</span>
              <div style={{ fontSize: '1.2rem', fontWeight: 600, color: metrics.disk_percent > 85 ? '#ef4444' : '#f59e0b' }}>
                {metrics.disk_percent != null ? `${metrics.disk_percent}%` : '--'}
              </div>
            </div>

            <div style={{ background: '#0e121b', border: '1px solid #1e2638', borderRadius: 6, padding: '10px 12px' }}>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>POSTGRESQL</span>
              <div style={{ fontSize: '1.2rem', fontWeight: 600, color: metrics.db_status === 'healthy' ? '#10b981' : '#f43f5e' }}>
                {metrics.db_status ? metrics.db_status.toUpperCase() : 'UNKNOWN'}
              </div>
            </div>

            <div style={{ background: '#0e121b', border: '1px solid #1e2638', borderRadius: 6, padding: '10px 12px' }}>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>APP VERSION</span>
              <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#e2e8f0' }}>
                {node.app_version || '2.0.0'}
              </div>
            </div>

            <div style={{ background: '#0e121b', border: '1px solid #1e2638', borderRadius: 6, padding: '10px 12px' }}>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>LAST HEARTBEAT</span>
              <div style={{ fontSize: '0.85rem', fontWeight: 500, color: '#94a3b8', marginTop: 4 }}>
                {node.last_heartbeat_at ? new Date(node.last_heartbeat_at).toLocaleTimeString() : 'Awaiting…'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bootstrap Command & File Preview Box */}
      {bootstrap && (
        <div style={{ marginTop: 20, padding: 14, background: '#090d16', border: '1px solid #26334d', borderRadius: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                onClick={() => setActiveBootstrapTab('command')}
                style={{ background: activeBootstrapTab === 'command' ? '#1e293b' : 'transparent', color: activeBootstrapTab === 'command' ? '#38bdf8' : '#94a3b8', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: '0.8rem' }}
              >
                Install Command
              </button>
              <button
                type="button"
                onClick={() => setActiveBootstrapTab('docker-compose')}
                style={{ background: activeBootstrapTab === 'docker-compose' ? '#1e293b' : 'transparent', color: activeBootstrapTab === 'docker-compose' ? '#38bdf8' : '#94a3b8', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: '0.8rem' }}
              >
                docker-compose.yml
              </button>
              <button
                type="button"
                onClick={() => setActiveBootstrapTab('installer-script')}
                style={{ background: activeBootstrapTab === 'installer-script' ? '#1e293b' : 'transparent', color: activeBootstrapTab === 'installer-script' ? '#38bdf8' : '#94a3b8', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontSize: '0.8rem' }}
              >
                install.sh
              </button>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => copy(activeBootstrapTab === 'command' ? bootstrap.install_command : (activeBootstrapTab === 'docker-compose' ? bootstrap.docker_compose : bootstrap.bootstrap_script))}
            >
              <Copy size={13} style={{ marginRight: 4 }} /> Copy {activeBootstrapTab === 'command' ? 'Command' : 'File'}
            </Button>
          </div>

          {activeBootstrapTab === 'command' && (
            <code style={{ display: 'block', padding: 8, background: '#04070d', color: '#10b981', borderRadius: 4, fontSize: '0.8rem', wordBreak: 'break-all' }}>
              {bootstrap.install_command}
            </code>
          )}

          {activeBootstrapTab === 'docker-compose' && (
            <pre style={{ margin: 0, padding: 8, background: '#04070d', color: '#e2e8f0', borderRadius: 4, fontSize: '0.75rem', overflowX: 'auto', maxHeight: 220 }}>
              {bootstrap.docker_compose}
            </pre>
          )}

          {activeBootstrapTab === 'installer-script' && (
            <pre style={{ margin: 0, padding: 8, background: '#04070d', color: '#e2e8f0', borderRadius: 4, fontSize: '0.75rem', overflowX: 'auto', maxHeight: 220 }}>
              {bootstrap.bootstrap_script}
            </pre>
          )}
        </div>
      )}
    </section>
  );
}
